import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';
import ConfirmDialog from '../../components/ConfirmDialog';

interface UnsavedChangesContextType {
	/** Whether the currently-mounted editor has unsaved changes. */
	isDirty: boolean;
	/** Report the active editor's unsaved-changes state. */
	setDirty: (dirty: boolean) => void;
	/**
	 * Run `action` immediately when there are no unsaved changes; otherwise
	 * defer it behind a discard-changes confirmation. Use this to gate any
	 * navigation that would unmount the active editor — subtab switches, admin
	 * panel tabs, sidebar links, etc.
	 */
	guard: (action: () => void) => void;
}

const UnsavedChangesContext = createContext<
	UnsavedChangesContextType | undefined
>(undefined);

export const UnsavedChangesProvider: React.FC<{
	children: React.ReactNode;
}> = ({ children }) => {
	const [isDirty, setIsDirty] = useState(false);
	const [pendingAction, setPendingAction] = useState<(() => void) | null>(
		null,
	);

	const setDirty = useCallback((dirty: boolean) => {
		setIsDirty(dirty);
	}, []);

	const guard = useCallback(
		(action: () => void) => {
			if (isDirty) setPendingAction(() => action);
			else action();
		},
		[isDirty],
	);

	// Warn on hard browser navigation (refresh / close / external link) while
	// there are unsaved changes. In-app navigation is handled by `guard`.
	useEffect(() => {
		if (!isDirty) return;
		const handler = (e: BeforeUnloadEvent) => {
			e.preventDefault();
			e.returnValue = '';
		};
		window.addEventListener('beforeunload', handler);
		return () => window.removeEventListener('beforeunload', handler);
	}, [isDirty]);

	const handleConfirm = () => {
		const action = pendingAction;
		setPendingAction(null);
		// Discarding leaves the current editor; clear dirtiness so a stale flag
		// can't block the next navigation before a fresh editor reports in.
		setIsDirty(false);
		action?.();
	};

	return (
		<UnsavedChangesContext.Provider value={{ isDirty, setDirty, guard }}>
			{children}
			<ConfirmDialog
				open={pendingAction !== null}
				title="Discard unsaved changes?"
				message="You have unsaved changes that will be lost if you leave. Do you want to discard them?"
				confirmLabel="Discard changes"
				cancelLabel="Keep editing"
				onConfirm={handleConfirm}
				onCancel={() => setPendingAction(null)}
			/>
		</UnsavedChangesContext.Provider>
	);
};

export const useUnsavedChanges = () => {
	const context = useContext(UnsavedChangesContext);
	if (!context)
		throw new Error(
			'useUnsavedChanges must be used within an UnsavedChangesProvider',
		);
	return context;
};
